import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, skip } from 'rxjs/operators';
import { CombinatedDroits } from 'src/app/utils/interfaces/new/combinateddroits.interface';
import { FormTemplate } from 'src/app/utils/interfaces/new/form-template.interfac';
import { InputTemplate } from 'src/app/utils/interfaces/new/input-template.interface';
import { TableRadioButtonLabelValue } from 'src/app/utils/interfaces/old/table-radio-button-interface';
import { environment } from 'src/environments/environment';

export const CLICK_OUTSIDE_MODAL = 'clickOutsideModal';

@Component({
  selector: 'app-add-edit',
  templateUrl: './add-edit.fragment.html',
  styleUrls: [
    './add-edit.fragment.scss',
    '../modal.fragment.scss'
  ]
})

// Ce fragment sera amené a évolué
export class AddEditFragment implements OnInit {

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  public modalTitle: string;
  public isEdit = false;
  public objectToEdit: any;
  public formGroup: FormGroup;
  public isError = false;
  public errorMessage = '';
  public droits: CombinatedDroits;
  public formTemplate: FormTemplate = { inputs: [] };
  public cancelButtonText = 'Quitter';
  public updateButtonText = 'Modifier';
  public validateButtonText = 'Ajouter';
  public dismissable = true;
  public skipValueChangeFunc = 1;
  public customCssClass = '';

  public keysHeaderLabel = 'Domaines';
  public keysHeaderValue = [
    { label: 'Contrôle total', value: '3' },
    { label: 'Ecriture', value: '2' },
    { label: 'Lecture', value: '1' },
    { label: 'Aucun', value: '0' },
  ];
  public bodyValues = [
    {
      label: 'Administration',
      value: null
    },
    {
      label: 'Compta',
      value: null
    },
    {
      label: 'Patient',
      value: null
    },
    {
      label: 'Agenda',
      value: null
    },
    {
      label: 'Honoraires',
      value: null
    },
    {
      label: 'Outils',
      value: null
    },
    {
      label: 'Planning',
      value: null
    }
  ];
  public radioTableDefinition = { headerLabel: this.keysHeaderLabel, headerValues: this.keysHeaderValue };

  @Output() public closeEmitter = new EventEmitter();
  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!environment.closeModalOnClickOutside) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target) && this.dismissable) {
      this.closeAddEditModal(CLICK_OUTSIDE_MODAL);
    }
  }

  constructor() { }

  ngOnInit(): void {
    let controlsGroup = {};
    this.formTemplate.inputs.forEach(inputTemplate => {
      controlsGroup = this.addControlToGroup(inputTemplate, controlsGroup);
      if (inputTemplate.subInputs && inputTemplate.subInputs.length > 0) {
        inputTemplate.subInputs.forEach(subInput => {
          controlsGroup = this.addControlToGroup(subInput, controlsGroup);
        });
      }
    });
    this.formGroup = new FormGroup(controlsGroup);
    if (this.formTemplate.validators) {
      this.formGroup.setValidators(this.formTemplate.validators);
    }

    this.formTemplate.inputs.forEach(inputTemplate => {
      if (inputTemplate.valueChangeFunction && controlsGroup[inputTemplate.id]) {
        (controlsGroup[inputTemplate.id] as FormControl).valueChanges.pipe(skip(this.skipValueChangeFunc)).pipe(debounceTime(0)).subscribe(v => {
          inputTemplate.valueChangeFunction(v);
        });
      }
    });
  }

  addControlToGroup(inputTemplate: InputTemplate, controlsGroup: any) {
    let control: FormControl;
    if (inputTemplate.type === 'ngSelect') {
      control = new FormControl(this.objectToEdit ? this.objectToEdit[inputTemplate.variable] : undefined, inputTemplate.validators);
    } else if (inputTemplate.type === 'autocomplete') {
      control = new FormControl(this.objectToEdit ? this.objectToEdit[inputTemplate.variable] : undefined, inputTemplate.validators);
    } else {
      control = new FormControl(this.objectToEdit ? this.objectToEdit[inputTemplate.variable] : '', inputTemplate.validators);
    }
    if (inputTemplate.required) {
      control.setValidators(Validators.required);
    }
    if (inputTemplate.validators?.length > 0) {
      control.setValidators(inputTemplate.validators);
    }
    controlsGroup[inputTemplate.id] = control;
    if (inputTemplate.type === 'radio-table') {
      if (this.objectToEdit) {
        this.bodyValues = this.objectToEdit[inputTemplate.variable].map((value) => { return { label: value.label, value: value.value.toString() }; });
      } else {
        this.bodyValues = this.droits.droits.map((droit) => { return { label: droit.nom, value: droit.acces.toString() }; })
      }
      this.mapDisabledValue(this.bodyValues);
    }
    return controlsGroup;
  }

  public closeAddEditModal(object?: any): void {
    if (object) {
      // object.droitsDelegation = this.bodyValues;
      this.closeEmitter.emit(object);
    } else {
      this.closeEmitter.emit(null);
    }
  }

  public stopPropagation(event): void {
    event.stopPropagation();
  }

  public async onSubmit(): Promise<void> {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.valid) {
      let object: any = {};
      if (this.isEdit) {
        object = { ...this.objectToEdit };
      }
      this.formTemplate.inputs.forEach(inputTemplate => {
        object = this.constructObject(inputTemplate, object);
        if (inputTemplate.subInputs && inputTemplate.subInputs.length > 0) {
          inputTemplate.subInputs.forEach(subInput => {
            object = this.constructObject(subInput, object);
          });
        }
      });
      this.closeAddEditModal(object);
    }
  }

  constructObject(inputTemplate: InputTemplate, object: any) {
    if (inputTemplate.type === 'radio-table') {
      object[inputTemplate.variable] = this.bodyValues;
    } else if (this.formGroup?.controls?.[inputTemplate.id]?.value
      || this.formGroup?.controls?.[inputTemplate.id]?.value === ""
      || this.formGroup?.controls?.[inputTemplate.id]?.value === 0) {
      object[inputTemplate.variable] = this.formGroup.controls[inputTemplate.id].value;
    }
    return object;
  }

  mapDisabledValue(bodyValues: TableRadioButtonLabelValue[]) {
    this.droits.droits.forEach((droit) => {
      const bodyValue = bodyValues.find((bodyValue) => droit.nom == bodyValue.label);
      if (bodyValue) {
        bodyValue.disabledValues = [];
        for (let valueDroit = 0; valueDroit < 4; valueDroit++) {
          if (!this.isEnable(valueDroit, droit.acces)) {
            bodyValue.disabledValues.push(valueDroit.toString());
          }
        }
      }
    })
  }
  isEnable(givenDroitValue: number, actualDroit: number | undefined): boolean {
    let result = false;
    switch (actualDroit) {
      case 0:
        result = givenDroitValue == 0;
        break;
      case 1:
      case 2:
        // On donne uniquement le droit qu'on a ou 0
        result = givenDroitValue == actualDroit || givenDroitValue == 0;
        break;
      case 3:
        // Full right
        result = true;
        break;
      default:
        result = false;
    }
    return result;
  }

}
